/* eslint-disable react/no-unknown-property */
/** @jsx jsx */
import { jsx, css } from '@emotion/react';
import { useEffect, useCallback, useState } from 'react';
import { useParams, useHistory, useRouteMatch } from 'react-router-dom';
import queryString from 'query-string';
import { useTranslation } from 'react-i18next';

import Header from '@/components/header';
import Footer from '@/components/paybylink/footer';
import PayByLink from '@/components/paybylink';
import {
  LinkInfoData,
  IntentData,
  PayByLinkValues,
  Error as PBLError,
  RequiredShopperDataKey,
  ShippingAddress,
  ShopperData,
} from '@/components/paybylink/types';
import { StorageKey, clientSetStorage } from '@/utils/localStorage';
import { useMobile } from '@/hooks/useMediaQuery';
import routing from '@/constants/routing';
import Banner from '@/components/paybylink/banner';
import { useStore } from '@/providers/storeProvider';
import { ErrorMapping, ErrorType } from '@/components/error';
import { airwayClient, getLinkInfo } from '@/api';
import useQuery from '@/hooks/useQuery';
import { AxiosError } from 'axios';
import { airTracker, TimerKey } from '@/utils/airTracker';

export const containerCss = css`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;
export const headerCss = css`
  background: transparent;
  z-index: 1;
`;
type Params = {
  code: string;
  region: string;
};
const COMPONENT_NAME = 'pages/paybylink/prePayment';

// eslint-disable-next-line sonarjs/cognitive-complexity
const Index: React.FC = () => {
  const { i18n } = useTranslation();
  const isMobile = useMobile();
  const history = useHistory();
  const { path } = useRouteMatch();
  const isPreview = path === '/pay/link/preview';
  const { code, region } = useParams<Params>();
  const [data, setData] = useState<IntentData & LinkInfoData>();
  const [error, setError] = useState<PBLError>();
  const [submitting, setSubmitting] = useState(false);
  const { payByLinkFormData, setPayByLinkFormData, setIntentData, setShopperData } = useStore();

  const fetchLinkInfo = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-shadow
    async (code: string) => {
      try {
        airTracker.timeStart(TimerKey.prepaymentGetLinkInfo);
        const response = await getLinkInfo(region, code);
        airTracker.timeEnd(TimerKey.prepaymentGetLinkInfo);
        if (response.data.type === 'ok') {
          const result = response.data.data;
          airTracker.info('getLinkInfoOk', { component: COMPONENT_NAME, result });
          setData(result);
          if (result.requiredShopperData) {
            const formData = {
              ...payByLinkFormData,
            };
            Object.keys(result.requiredShopperData).forEach((key) => {
              if (!result.requiredShopperData[key as RequiredShopperDataKey]) {
                delete formData[key as RequiredShopperDataKey];
              }
            });
            setPayByLinkFormData(formData);
          }
          return response;
        }
        if (response.data.type === 'error') {
          setError(response?.data?.error as PBLError);
        }
        // eslint-disable-next-line @typescript-eslint/no-shadow
      } catch (e) {
        const err = e as AxiosError;
        airTracker.warn('getLinkInfoError', { component: COMPONENT_NAME, err: err?.response?.data });
        setError(err?.response?.data as PBLError);
      }
      return undefined;
    },
    [region]
  );
  const { data: mockData, lang } = useQuery<{ data: string; lang: string }>();
  const mockLinkInfo = useCallback(() => {
    if (lang) {
      i18n.changeLanguage(lang);
    }
    const result = JSON.parse(decodeURIComponent(mockData));
    airTracker.info('prePaymentMockInfo', { component: COMPONENT_NAME, result });
    setData(result);
  }, [mockData, i18n, lang]);

  const redirectToError = useCallback(
    (key: keyof typeof ErrorMapping, logoUrl: string) => {
      airTracker.info('redirectToError', { component: COMPONENT_NAME, error: key });
      history.push(`${routing.payByLinkPaymentError}?${queryString.stringify(ErrorMapping[key])}&logoUrl=${logoUrl}`);
    },
    [history]
  );

  const redirectToPBLCheckout = useCallback(
    (intentData: IntentData) => {
      setIntentData(intentData);
      const params = {
        intent_id: intentData?.intent_id,
        client_secret: intentData?.secret,
        currency: intentData?.amount?.ccy,
        amount: intentData?.amount?.amount,
        countryCode: intentData?.merchant_country_code || '',
        title: data?.title,
        logoUrl: data?.logoUrl || '',
        merchant: data?.merchant || '',
        description: data?.description || '',
        code,
      };
      history.push(`${routing.payByLinkPayment(intentData.intent_id)}?${queryString.stringify(params)}`);
    },
    [data]
  );

  const onPay = useCallback(
    async (values: PayByLinkValues) => {
      airTracker.info('onPay', { component: COMPONENT_NAME, formValues: values });
      airTracker.timeStart(TimerKey.payByLinkOnPay);
      setSubmitting(true);
      try {
        const linkRes = await getLinkInfo(region, code);
        if (linkRes.data?.data?.status === 'PAID') {
          redirectToError(ErrorType.PAID, linkRes.data?.data?.logoUrl);
          return;
        }
      } catch (e) {
        airTracker.warn('onPayError', { component: COMPONENT_NAME, error: e });
      }

      setPayByLinkFormData(values);
      let amount: LinkInfoData['amount'];
      const isOneTimeFixedAmount = data?.amount?.amount;
      const isReusableFixedCurrency = data?.minAmount?.ccy;
      if (isOneTimeFixedAmount) {
        amount = {
          ccy: data?.amount?.ccy ?? '',
          amount: data?.amount.amount ?? 0,
        };
      } else if (isReusableFixedCurrency) {
        amount = {
          ccy: data?.minAmount?.ccy ?? '',
          amount: Number(values.amount.replace(/,/g, '')),
        };
      } else {
        amount = {
          ccy: values.currency,
          amount: Number(values.amount.replace(/,/g, '')),
        };
      }
      try {
        const shopperData: ShopperData = {
          sendEmail: values.sendEmail,
        };
        Object.keys(data.requiredShopperData).forEach((key) => {
          if (data.requiredShopperData[key as RequiredShopperDataKey]) {
            shopperData[key as RequiredShopperDataKey] = values[key as RequiredShopperDataKey] as string &
              ShippingAddress;
          }
        });
        const response = await airwayClient.post(`/payment-link${region ? `/${region}` : ''}/shopper/${code}/pay`, {
          amount,
          shopper: shopperData,
        });
        airTracker.timeEnd(TimerKey.payByLinkOnPay);
        setSubmitting(false);
        if (response.data.type === 'ok') {
          setShopperData(shopperData);
          redirectToPBLCheckout(response.data.data);
        } else if (response.data.type === 'error') {
          setError(response?.data.error);
        }
        // eslint-disable-next-line @typescript-eslint/no-shadow
      } catch (e) {
        airTracker.warn('onPayError2', { component: COMPONENT_NAME, error: e });
        const err = e as AxiosError;
        setSubmitting(false);
        setError(err.response?.data as PBLError);
      }
    },
    [code, data, region]
  );
  useEffect(() => {
    if (isPreview) {
      mockLinkInfo();
    } else {
      fetchLinkInfo(code);
    }
  }, [code, fetchLinkInfo, mockLinkInfo, isPreview]);

  useEffect(() => {
    if (isPreview) {
      return;
    }
    if (['PAID', 'EXPIRED', 'DISABLED'].includes(data?.status)) {
      redirectToError(data.status as keyof typeof ErrorMapping, data?.logoUrl);
    }
    clientSetStorage(StorageKey.IS_PAYMENT_LINK_AMOUNT_SET, 'true');
  }, [data, isPreview, redirectToError]);

  if (!data && !error) {
    return <span data-testid="pay-by-link" />;
  }
  return (
    <div css={containerCss} data-testid="pay-by-link">
      <Header isMobile={isMobile} haveLanguageChoice={true} logoUrl={data?.logoUrl} />
      <div
        css={css`
          display: flex;
          flex: 1;
          margin-bottom: 90px;
        `}
      >
        {data?.status === 'ACTIVE' ? (
          <PayByLink
            error={error}
            linkInfo={data}
            onSubmit={onPay}
            submitting={submitting}
            initialValues={payByLinkFormData}
          />
        ) : (
          <Banner hasError={error?.type === 'CODE_NOT_FOUND' || error?.code === 'resource_not_found'} linkInfo={data} />
        )}
      </div>
      {isMobile && <Footer />}
    </div>
  );
};
export default Index;
